import type { ProWebsiteFeaturesLobsDtoLobSegmentData } from "@similarweb/pro-api-client";

/**
 * Lines of Business Utilities
 *
 * Lines of Business segments represent business verticals/departments within a company
 * (e.g., "Marketing", "Sales", "Support" for a domain like "hubspot.com").
 *
 * For backend implementation convenience, Lines of Business are stored as "segments" in the database
 * using a special prefix pattern, but they have different business logic than regular
 * custom segments (different sharing rules, validation, etc.).
 *
 * This file consolidates all Lines of Business-related utilities to maintain consistency
 * and avoid duplicate logic across the codebase.
 */

// Lines of Business segment ID prefix used to distinguish them from regular segments
export const CUSTOM_LINES_OF_BUSINESS_SEGMENT_PREFIX = "$LOB";

// Pattern: $LOB_<domain>;<businessLineName> (e.g., "$LOB_hubspot.com;Marketing")
// Capture groups: [fullMatch, domain, businessLineName]
export const LINES_OF_BUSINESS_SEGMENT_ID_REGEX = /^\$LOB_(.+?);(.+)$/;

/**
 * Checks if a segment ID represents a Lines of Business segment
 */
export const isLinesOfBusinessSegment = (segmentId: string | undefined | null): boolean => {
    if (!segmentId) {
        return false;
    }
    return segmentId.startsWith(CUSTOM_LINES_OF_BUSINESS_SEGMENT_PREFIX);
};

/**
 * Creates a Lines of Business segment ID from domain and business line name
 * @param domain - The website domain (e.g., "hubspot.com")
 * @param businessLineName - The line of business name (e.g., "Marketing")
 * @returns Lines of Business segment ID (e.g., "$LOB_hubspot.com;Marketing")
 */
export const createLinesOfBusinessSegmentId = (domain: string, businessLineName: string): string => {
    return `${CUSTOM_LINES_OF_BUSINESS_SEGMENT_PREFIX}_${domain};${businessLineName}`;
};

/**
 * Parses a Lines of Business segment ID and extracts the domain and business line name
 * @param linesOfBusinessSegmentId - The Lines of Business segment ID (e.g., "$LOB_hubspot.com;Marketing")
 * @returns Object with domain and businessLineName, or null if not a valid Lines of Business segment ID
 */
export const parseLinesOfBusinessSegmentId = (
    linesOfBusinessSegmentId: string,
): { domain: string; businessLineName: string } | null => {
    if (!isLinesOfBusinessSegment(linesOfBusinessSegmentId)) {
        return null;
    }

    const match = linesOfBusinessSegmentId.match(LINES_OF_BUSINESS_SEGMENT_ID_REGEX);
    if (match) {
        const [, domain, businessLineName] = match; // Skip fullMatch at index 0
        return { domain, businessLineName };
    }

    return null;
};

/**
 * Filters a list of segments to return only custom segments (excludes Lines of Business)
 * Useful for functions that should only process actual custom segments
 */
export const getCustomSegments = (segments: string[] = []): string[] => {
    return segments.filter((segmentId) => !isLinesOfBusinessSegment(segmentId));
};

/**
 * Filters a list of segments to return only Lines of Business segments
 */
export const getLinesOfBusinessSegments = (segments: string[] = []): string[] => {
    return segments.filter(isLinesOfBusinessSegment);
};

/**
 * Checks if an industry/category has any custom segments (excluding Lines of Business)
 */
export const hasCustomSegments = (segments: string[] = []): boolean => {
    return getCustomSegments(segments).length > 0;
};

export const hasLinesOfBusinessSegments = (segments: string[] = []): boolean => {
    return getLinesOfBusinessSegments(segments).length > 0;
};

/**
 * Computes the difference between two sets (items in setA that are not in setB)
 * Uses native Set.difference() if available, falls back to filter for older environments (Playwright etc.)
 */
const setDifference = <T>(setA: Set<T>, setB: Set<T>): Set<T> => {
    if (typeof setA.difference === "function") {
        return setA.difference(setB);
    }
    // Fallback for environments without Set.prototype.difference
    return new Set([...setA].filter((item) => !setB.has(item)));
};

export const calculateAvailableLinesOfBusinessCount = (
    linesOfBusinessData: ProWebsiteFeaturesLobsDtoLobSegmentData[] | undefined,
    excludeItems: string[] = [],
    excludedDomains: string[] = [],
): number => {
    if (!linesOfBusinessData?.length) {
        return 0;
    }

    const excludedDomainsSet = new Set(excludedDomains);
    // Filter out items whose domain is already selected as a website
    const availableItems = linesOfBusinessData.filter(
        (item) =>
            // @ts-ignore FIXME: strict migration - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'
            !excludedDomainsSet.has(item.Site),
    );

    const allLinesOfBusinessIds = new Set(
        availableItems.map((item) =>
            // @ts-ignore FIXME: strict migration - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'
            createLinesOfBusinessSegmentId(item.Site, item.Lob),
        ),
    );
    const excludeSet = new Set(excludeItems);

    return setDifference(allLinesOfBusinessIds, excludeSet).size;
};
